import React, { useState, useEffect, useCallback } from 'react';
import { distanceCacheBody } from '@carboncare/schemas';
import { preflight } from '../../services/api/schemaPreflight';
import { formatTransportMode } from '../../utils/transportModeDisplay';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Grid,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import apiClient from '../../services/api';
import { useCoordinatePaste } from '../../hooks/useCoordinatePaste';
import { displayLocation, normalizeLocation } from '../../utils/locationDisplay';
import PageHeader from '../../components/common/PageHeader';
import {
  formatCoord4,
  StatusSnackbars,
  usePaginationState,
  useReferenceSearch,
  NoResultsRow,
  SearchBarPaper,
  CrudTablePagination,
  DeleteConfirmDialog,
  SkeletonActionCell,
  CrudFormDialog,
  DecimalTextField,
  validateDecimal,
} from './_shared';

interface DistanceCacheEntry {
  id?: number;
  origin?: string;
  destination?: string;
  from_location?: string;
  to_location?: string;
  from_lat?: number;
  from_lon?: number;
  to_lat?: number;
  to_lon?: number;
  distance_km?: number;
  distance_miles?: number;
  duration_seconds?: number;
  transport_mode: string;
  route_type?: string;
  route_details?: any;
  data_source?: string;
  provider?: string;
  expires_at?: string;
  created_at?: string;
  updated_at?: string;
  calculated_at?: string;
  hit_count?: number;
}


const DistanceCachePage: React.FC = () => {
  const [cacheEntries, setCacheEntries] = useState<DistanceCacheEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const { page, setPage, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePaginationState(20);
  const { search, matchMode, searchProps } = useReferenceSearch(setPage);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<DistanceCacheEntry | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState<'add' | 'edit'>('add');
  const [editingEntry, setEditingEntry] = useState<Partial<DistanceCacheEntry>>({
    from_location: '',
    to_location: '',
    distance_km: undefined,
    transport_mode: 'ROAD'
  });
  const [routeDetailsJson, setRouteDetailsJson] = useState<string>('');
  const [jsonError, setJsonError] = useState<string>('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleFromPaste = useCoordinatePaste(setEditingEntry, setFormErrors, 'from_lat', 'from_lon');
  const handleToPaste = useCoordinatePaste(setEditingEntry, setFormErrors, 'to_lat', 'to_lon');

  const handleFormChange = (field: keyof DistanceCacheEntry) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: any } }
  ) => {
    let value: any = event.target.value;

    const updates: Partial<DistanceCacheEntry> = {};

    // A half-typed decimal ("-", ".") parses to NaN — keep it out of the entry
    const toNumber = (raw: any): number | undefined => {
      const parsed = Number.parseFloat(raw);
      return Number.isNaN(parsed) ? undefined : parsed;
    };

    if (field === 'from_lat' || field === 'from_lon' || field === 'to_lat' || field === 'to_lon') {
        value = toNumber(value);
        updates[field] = value;
    } else if (field === 'distance_km') {
        value = toNumber(value);
        updates.distance_km = value;
        updates.distance_miles = value === undefined ? undefined : value * 0.621371;
    } else if (field === 'distance_miles') {
        value = toNumber(value);
        updates.distance_miles = value;
        updates.distance_km = value === undefined ? undefined : value / 0.621371;
    } else {
        updates[field] = value;
    }

    setEditingEntry(prev => ({ ...prev, ...updates }));

    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const fetchCacheEntries = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.get('/data-management/distance-cache', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          matchMode,
          sortBy: 'created_at',
          sortOrder: 'desc'
        }
      });

      if (response.data.success) {
        setCacheEntries(response.data.data);
        setTotalCount(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch cache entries');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, matchMode]);

  useEffect(() => {
    fetchCacheEntries();
  }, [fetchCacheEntries]);

  const handleDeleteClick = (entry: DistanceCacheEntry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;

    try {
      const response = await apiClient.delete(
        `/data-management/distance-cache/${entryToDelete.id}`
      );
      if (response.data.success) {
        setSuccess('Cache entry deleted successfully');
        fetchCacheEntries();

      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete cache entry');
    } finally {
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
    }
  };

  const handleSaveEntry = async () => {
    const newErrors: Record<string, string> = {};
    if (!editingEntry.from_location) newErrors.from_location = 'From Location is required';
    if (!editingEntry.to_location) newErrors.to_location = 'To Location is required';
    if (!editingEntry.transport_mode) newErrors.transport_mode = 'Transport Mode is required';
    const distanceError = validateDecimal(editingEntry.distance_km, 'Distance');
    if (distanceError) newErrors.distance_km = distanceError;
    // The four coordinates are optional, but a half-typed one must not reach the API as NaN.
    for (const field of ['from_lat', 'from_lon', 'to_lat', 'to_lon'] as const) {
      const value = editingEntry[field];
      if (value !== undefined && value !== null && !Number.isFinite(Number(value))) {
        newErrors[field] = 'Must be a number using "." as the decimal separator';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      const dataToSend = {
        from_location: normalizeLocation(editingEntry.from_location),
        to_location: normalizeLocation(editingEntry.to_location),
        from_lat: editingEntry.from_lat,
        from_lon: editingEntry.from_lon,
        to_lat: editingEntry.to_lat,
        to_lon: editingEntry.to_lon,
        distance_km: editingEntry.distance_km,
        distance_miles: editingEntry.distance_miles,
        transport_mode: editingEntry.transport_mode,
        route_type: editingEntry.route_type,
        route_details: editingEntry.route_details,
        data_source: editingEntry.data_source,
        expires_at: editingEntry.expires_at
      };

      if (editMode === 'edit' && editingEntry.id) {
        const response = await apiClient.put(
          `/data-management/distance-cache/${editingEntry.id}`,
          dataToSend
        );
        if (response.data.success) {
          setSuccess('Cache entry updated successfully');
        }
      } else {
        preflight(distanceCacheBody, dataToSend);
        const response = await apiClient.post('/data-management/distance-cache', dataToSend);
        if (response.data.success) {
          setSuccess('Cache entry added successfully');
        }
      }

      handleCloseDialog();
      fetchCacheEntries();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save cache entry');
    }
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setFormErrors({});
  };

  return (
    <Box>
      <PageHeader
        description="Inspect and manage cached distance lookups."
        actions={
          <>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setEditMode('add');
                setEditingEntry({
                  from_location: '',
                  to_location: '',
                  distance_km: undefined,
                  transport_mode: 'ROAD'
                });
                setRouteDetailsJson('');
                setJsonError('');
                setFormErrors({});
                setEditDialogOpen(true);
              }}
              variant="contained"
              color="primary"
            >
              Add Entry
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchCacheEntries();

              }}
              variant="outlined"
            >
              Refresh
            </Button>
          </>
        }
      />

      <StatusSnackbars
        error={error}
        success={success}
        onClearError={() => setError('')}
        onClearSuccess={() => setSuccess('')}
      />

      {/* Statistics Cards */}
      <SearchBarPaper
        placeholder="Search by origin or destination..."
        {...searchProps}
      />

      <Paper>
        <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Origin</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Distance</TableCell>
                  <TableCell>From Coordinates</TableCell>
                  <TableCell>To Coordinates</TableCell>
                  <TableCell>Transport</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: 10 }, (_, i) => `skeleton-${i}`).map((rowKey, idx) => (
                      <TableRow key={rowKey}>
                        <TableCell><Skeleton variant="text" width={`${50 + ((idx * 7) % 30)}%`} /></TableCell>
                        <TableCell><Skeleton variant="text" width={`${45 + ((idx * 11) % 35)}%`} /></TableCell>
                        <TableCell><Skeleton variant="text" width={70} /></TableCell>
                        <TableCell><Skeleton variant="text" width={130} sx={{ fontSize: '0.75rem' }} /></TableCell>
                        <TableCell><Skeleton variant="text" width={130} sx={{ fontSize: '0.75rem' }} /></TableCell>
                        <TableCell><Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                        <TableCell><Skeleton variant="text" width={84} /></TableCell>
                        <SkeletonActionCell />
                      </TableRow>
                    ))
                  : cacheEntries.length === 0
                    ? <NoResultsRow colSpan={8} search={search} matchMode={matchMode} />
                    : cacheEntries.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell>{displayLocation(entry.origin || entry.from_location)}</TableCell>
                    <TableCell>{displayLocation(entry.destination || entry.to_location)}</TableCell>
                    <TableCell>{entry.distance_km == null ? '0' : Number(entry.distance_km).toFixed(2)} km</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatCoord4(entry.from_lat)},
                        {' '}{formatCoord4(entry.from_lon)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatCoord4(entry.to_lat)},
                        {' '}{formatCoord4(entry.to_lon)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatTransportMode(entry.transport_mode)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditMode('edit');
                            const entryData = {
                              ...entry,
                              from_location: displayLocation(entry.origin || entry.from_location),
                              to_location: displayLocation(entry.destination || entry.to_location)
                            };
                            setEditingEntry(entryData);
                            setRouteDetailsJson(
                              entryData.route_details
                                ? JSON.stringify(entryData.route_details, null, 2)
                                : ''
                            );
                            setJsonError('');
                            setFormErrors({});
                            setEditDialogOpen(true);
                          }}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(entry)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        </TableContainer>
        <CrudTablePagination
          loading={loading}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Are you sure you want to delete this cache entry?
        <br />
        <strong>{displayLocation(entryToDelete?.origin || entryToDelete?.from_location)}</strong> → <strong>{displayLocation(entryToDelete?.destination || entryToDelete?.to_location)}</strong>
      </DeleteConfirmDialog>


      <CrudFormDialog
        open={editDialogOpen}
        title={editMode === 'add' ? 'Add Distance Cache Entry' : 'Edit Distance Cache Entry'}
        onClose={handleCloseDialog}
        onSave={handleSaveEntry}
        saveLabel={editMode === 'add' ? 'Add' : 'Update'}
        saveButtonProps={{ color: 'primary', disabled: !!jsonError }}
      >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="From Location"
                value={editingEntry.from_location || ''}
                onChange={handleFormChange('from_location')}
                required
                error={!!formErrors.from_location}
                helperText={formErrors.from_location}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="To Location"
                value={editingEntry.to_location || ''}
                onChange={handleFormChange('to_location')}
                required
                error={!!formErrors.to_location}
                helperText={formErrors.to_location}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DecimalTextField
                fullWidth
                label="From Latitude"
                value={editingEntry.from_lat}
                onChange={handleFormChange('from_lat')}
                onPaste={handleFromPaste}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DecimalTextField
                fullWidth
                label="From Longitude"
                value={editingEntry.from_lon}
                onChange={handleFormChange('from_lon')}
                onPaste={handleFromPaste}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DecimalTextField
                fullWidth
                label="To Latitude"
                value={editingEntry.to_lat}
                onChange={handleFormChange('to_lat')}
                onPaste={handleToPaste}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DecimalTextField
                fullWidth
                label="To Longitude"
                value={editingEntry.to_lon}
                onChange={handleFormChange('to_lon')}
                onPaste={handleToPaste}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DecimalTextField
                fullWidth
                label="Distance (km)"
                value={editingEntry.distance_km}
                onChange={handleFormChange('distance_km')}
                required
                error={!!formErrors.distance_km}
                helperText={formErrors.distance_km}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DecimalTextField
                fullWidth
                label="Distance (miles)"
                value={editingEntry.distance_miles}
                onChange={handleFormChange('distance_miles')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Transport Mode</InputLabel>
                <Select
                  value={editingEntry.transport_mode || 'ROAD'}
                  onChange={handleFormChange('transport_mode')}
                  label="Transport Mode"
                  disabled={editMode === 'edit'}
                  error={!!formErrors.transport_mode}
                >
                  <MenuItem value="ROAD">Road</MenuItem>
                  <MenuItem value="AIR">Air</MenuItem>
                  <MenuItem value="SEA">Sea</MenuItem>
                  <MenuItem value="RAIL">Rail</MenuItem>
                  <MenuItem value="IWW">IWW</MenuItem>
                  <MenuItem value="FERRY">Ferry</MenuItem>
                </Select>
                {formErrors.transport_mode && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{formErrors.transport_mode}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Route Type"
                value={editingEntry.route_type || ''}
                onChange={handleFormChange('route_type')}
                placeholder="e.g., direct, via hub"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data Source"
                value={editingEntry.data_source || ''}
                onChange={handleFormChange('data_source')}
                disabled={editMode === 'edit'}
                placeholder="e.g., google maps, osrm"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Route Details (JSON)"
                value={routeDetailsJson}
                onChange={(e) => {
                  const value = e.target.value;
                  setRouteDetailsJson(value);

                  // Validate JSON
                  if (!value.trim()) {
                    setJsonError('');
                    setEditingEntry({ ...editingEntry, route_details: null });
                    return;
                  }

                  try {
                    const parsed = JSON.parse(value);
                    setEditingEntry({ ...editingEntry, route_details: parsed });
                    setJsonError('');
                  } catch (err) {
                    console.error('Invalid JSON format for route details:', err);
                    setJsonError('Invalid JSON format');
                  }
                }}
                multiline
                rows={4}
                placeholder='{"via": ["hub1", "hub2"], "type": "direct"}'
                error={!!jsonError}
                helperText={jsonError || 'Enter valid JSON for route details'}
              />
            </Grid>
          </Grid>
      </CrudFormDialog>
    </Box>
  );
};

export default DistanceCachePage;
